import { Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { AlertController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import * as mobx from 'mobx';
import { cmp } from 'semver';
import { UserStore } from 'user.store';
import { environment } from '../../environments/environment';
import { FirebaseService } from './firebase.service';
import type { RecallResponse, RecallSet, RecallSets } from './recall.service.const';
import { APP_CONSTANTS } from 'app/app.constants';

@Injectable({
  providedIn: 'root'
})
export class RecallService {
  private readonly mockVersionNumber = '1.0.0' as const;

  constructor(
    private readonly alertCtrl: AlertController,
    private readonly firebase: FirebaseService,
    private readonly platform: Platform,
    private readonly translate: TranslateService,
    private readonly userStore: UserStore,
  ) {
    // noop
  }

  private get marketId(): ISOCountry {
    const [, marketId] = this.translate.currentLang.split('-') as [ISOLanguage, ISOCountry];

    return marketId;
  }

  private get recallSets(): RecallSets {
    return mobx.toJS(this.userStore?.remoteConfig?.newton?.recallSets ?? {});
  }

  private get recallSet(): RecallSet {
    return this.recallSets[this.marketId] ?? {};
  }

  private get recallResponse(): RecallResponse {
    const platforms = this.platform.platforms() as ReadonlyArray<keyof RecallSet>;

    const selected = platforms.find(platform => this.recallSet[platform]);

    const recall = this.recallSet[selected] ?? {} as RecallResponse;

    this.userStore.updateUser({ recall });

    return recall;
  }

  private get isRecalled(): boolean {
    return this.recallResponse?.recalled ?? false;
  }

  private get isDecommissioned(): boolean {
    return this.recallResponse?.decommissioned ?? false;
  }

  private get isInapp(): boolean {
    return this.recallResponse?.inapp ?? false;
  }

  private get minVersion(): string {
    return this.recallResponse?.minVersion ?? this.mockVersionNumber;
  }

  private get isLocaleSelected(): boolean {
    return !!this.userStore.locale && !!this.userStore.marketId;
  }

  private readonly openUrl = () => {
    const storeURL = this.platform.is('android') ? environment.androidAppStoreLink : environment.iosAppStoreLink;
    window.open(storeURL, '_system');

    return false;
  };

  public async checkAppRecallService(): Promise<RecallResponse> {
    if (this.platform.is('capacitor')) {
      await mobx.when(() => this.isLocaleSelected);
    }
    await this.firebase.refresh();

    if (this.isRecalled) {
      return this.showRecallAlerts(APP_CONSTANTS.RECALL.RECALLED);
    } else if (this.isDecommissioned) {
      this.showRecallAlerts(APP_CONSTANTS.RECALL.DECOMMISSIONED);
    } else if (this.isInapp) {
      this.showRecallAlerts(APP_CONSTANTS.RECALL.INAPP, 'OK');
    }

    const currentAppVersion = this.platform.is('capacitor') ? (await App.getInfo()).version : this.mockVersionNumber;
    if (cmp(currentAppVersion, '<', this.minVersion)) {

      return this.showRecallAlerts(APP_CONSTANTS.RECALL.UPGRADE, 'UPDATE');
    }

    return this.recallResponse;
  }

  public async showRecallAlerts(recallType, buttonText?): Promise<RecallResponse> {
    let buttons;
    if (buttonText) {
      buttons = [{
        text: this.translate.instant(buttonText),
        handler: () => {
          this.recallType(recallType);
        }
      }];
    } else {
      buttons = [];
    }
    const modal = await this.alertCtrl.create({
      header: this.recallResponse.context[this.userStore.locale][recallType].header,
      message: this.recallResponse.context[this.userStore.locale][recallType].body,
      cssClass: 'recall-alert',
      buttons
    });

    await modal.present();

    return this.recallResponse;
  }

  private recallType(recallType) {
    if (recallType === APP_CONSTANTS.RECALL.UPGRADE) {
      this.openUrl();
    } else {
      this.alertCtrl.dismiss();
    }
  }

}
